import { FC } from 'react';
import { useHistory } from 'react-router';
import { Box } from '@chakra-ui/react';
import { Feature } from '@shinetools/pricing-plan-library';

import { getLocalizedErrorMessage } from 'common/errors';
import { getServerErrorCode } from 'common/errors/utils';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { useCompanySettingsQuery } from 'common/hooks/useCompanySettings';
import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import useFeatureFlagsSwitcher from 'common/hooks/useFeatureFlagsSwitcher';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import ModalProvider from 'components/ModalProvider';
import useToast from 'components/ToastProvider/useToast';
import { logEvent } from 'features/Analytics/analytics';
import { InvoiceFormSchema } from 'features/Work/form/invoiceForm.schema';
import { ConfirmType } from 'features/Work/types';
import { datetimeToLocalDateUtc } from 'helpers/date';

import * as routes from '../../../../routes';
import FormLayout from '../../components/FormLayout';
import {
  applyCacheToInvoice,
  applyPresetsToInvoice,
  defaultInvoice,
} from '../../components/FormLayout/constants';
import { useCachedInvoice } from '../../components/FormLayout/hooks/useCachedInvoice';
import { useCreateInvoice } from '../../hooks/mutations/useCreateInvoice';
import { formatCreateMutationInput } from './helpers/formatCreateMutationInput';
import locales from './locales';

const AddPage: FC = () => {
  const history = useHistory();
  const { showToast } = useToast();
  const { isFeatureAvailable } = useCurrentPricingPlan();
  const { companyProfileId } = useCompanyContext();
  const { cachedInvoice, clearCachedInvoice } = useCachedInvoice();
  const { featureFlags } = useFeatureFlagsSwitcher();

  const {
    companySettings,
    error: companySettingsError,
    loading: companySettingsLoading,
  } = useCompanySettingsQuery();

  const [createInvoice] = useCreateInvoice();

  // When coming from a client details page, pre-select the client
  const clientId = new URLSearchParams(location.search).get('clientId');

  if (companySettingsLoading) {
    return (
      <Box
        backgroundColor="white"
        bottom={0}
        left={0}
        position="absolute"
        right={0}
        top={0}
        zIndex={1}
      >
        <Loader />
      </Box>
    );
  }

  if (companySettingsError || !companySettings || !companyProfileId) {
    return <ErrorView />;
  }

  /**
   * For plans with items catalog enabled, we want to show an option to create the first task from the catalog
   * Therefore we don't want the initial default task included with the default invoice
   */
  const planDefaultInvoice = {
    ...defaultInvoice,
    ...(isFeatureAvailable(Feature.InvoicingItemsCatalog) ? { tasks: [] } : {}),
  };

  const onConfirm = async (invoice: InvoiceFormSchema, type: ConfirmType) => {
    try {
      const createInvoiceInput = formatCreateMutationInput(invoice);
      const createdInvoice = await createInvoice(
        {
          ...createInvoiceInput,
          settings: {
            ...createInvoiceInput.settings,
            displayTradeName: featureFlags.enableInvoicingTradeNameOption
              ? createInvoiceInput.settings?.displayTradeName
              : undefined,
          },
        },
        invoice.clientId,
      );
      const invoiceId =
        createdInvoice.data?.createInvoice.invoice.invoiceId ?? '';

      logEvent({
        name: 'Invoice Created',
        properties: { invoiceId },
      });
      clearCachedInvoice();

      if (type === 'save') {
        history.replace(routes.getEditInvoice(invoiceId));
        showToast({
          message: locales.success,
          type: 'success',
        });
        return;
      }

      history.replace(routes.getInvoice(invoiceId));
    } catch (err) {
      const serverErrorCode = getServerErrorCode(err);

      if (serverErrorCode !== undefined) {
        showToast({
          message:
            getLocalizedErrorMessage(serverErrorCode) ?? locales.defaultError,
          type: 'error',
        });
      }
    }
  };

  const onClose = () => {
    clearCachedInvoice();
    history.replace(routes.invoices);
  };

  /**
   * Update the invoice startAt value to make sure it is set to today's date
   * in case the invoice last edition was done on a previous day
   */
  const startAt = datetimeToLocalDateUtc(new Date()).valueOf();
  const initialInvoice =
    cachedInvoice && !clientId
      ? applyCacheToInvoice({
          baseInvoice: {
            ...planDefaultInvoice,
            companyProfileId,
            startAt,
          },
          cachedInvoice,
        })
      : applyPresetsToInvoice(
          {
            ...planDefaultInvoice,
            ...(clientId ? { clientId } : {}),
            companyProfileId,
            startAt,
          },
          companySettings,
        );

  return (
    <ModalProvider>
      <FormLayout
        companySettings={companySettings}
        invoice={initialInvoice}
        onClose={onClose}
        onConfirm={onConfirm}
      />
      ;
    </ModalProvider>
  );
};

export default AddPage;
